import React, { useMemo } from 'react';
import { RegisterConfig, RegisterModuleStateProps } from '@/components/modules/register/register-dynamic/types';
import { DEBUG_REGISTER_STEPS } from '@/components/modules/register/register-dynamic/const';

const initialConfig: RegisterConfig = {
  stepperContext: {
    hide: true,
  },
  globalInputContext: {},
  steps: [
    {
      stepperIndex: 1,
      components: [
        {
          layout: 'text',
          label: 'REGISTER_CONFIG_ERROR',
        },
      ],
    },
  ],
};

export function useRegisterConfig(
  state: RegisterModuleStateProps,
  setState: React.Dispatch<React.SetStateAction<RegisterModuleStateProps>>,
  propsConfig: any,
) {
  const [countInjectedSteps, setCountInjectedSteps] = React.useState(0);
  const [config, setConfig] = React.useState<RegisterConfig>(initialConfig);
  React.useEffect(() => {
    let conf: RegisterConfig;
    try {
      if (DEBUG_REGISTER_STEPS) {
        // @ts-ignore
        conf = DEBUG_REGISTER_STEPS ? DEBUG_REGISTER_STEPS : { ...initialConfig };
        console.warn('DEBUG_REGISTER_STEPS', config);
      } else {
        conf = propsConfig ? JSON.parse(propsConfig) : { ...initialConfig };
        if (conf.steps) {
          conf.steps.push({
            stepperIndex: conf.steps.length,
            components: [
              {
                layout: 'auto-login',
              },
            ],
          });
          setCountInjectedSteps(countInjectedSteps + 1);
        }
      }
    } catch (e) {
      conf = { ...initialConfig };
    }
    setConfig(conf);
    // DO NOT ADD countInjectedSteps, and setCountInjectedSteps TO THE DEPENDENCIES, IT WILL CAUSE AN INFINITE LOOP
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsConfig]);

  const stepsMap = React.useMemo(() => {
    let signupStep = config.steps.length - (countInjectedSteps + 1); // when the signup will be triggered;
    let lastStep = signupStep; // when the signup will be triggered; by default it's the last step -1
    let ocrStep = -1; // we use this to do some optimizations; -1 means it's not present
    let otpStep = -1; // we use this to do some optimizations; -1 means it's not present
    let marketingOfferStep = -1; // we use this to do some optimizations; -1 means it's not present
    let congratsModalStep = -1; // we use this to do some optimizations; -1 means it's not present
    let congratsElementStep = -1; // we use this to do some optimizations; -1 means it's not present
    let nameStep = -1; // we use this to do some optimizations; -1 means it's not present
    // we use this to do some optimizations; make it the last step by default
    // if no t&c step is found, it will remain the last step. this should be combined cu tcAcceptedByDefault
    let tcStep = signupStep;
    let autoLoginStep = -1; // we use this to do some optimizations; -1 means it's not present

    const inputs: { [key: string]: number } = {};

    config?.steps?.forEach((step, index) => {
      step.components.forEach((component) => {
        if (component.layout === 'ocr-scanner') {
          if (ocrStep === -1) {
            ocrStep = index;
          }
        } else if (component.layout === 'input') {
          if (component.type) {
            switch (component.type) {
              case 'otp':
                if (otpStep === -1) {
                  otpStep = index;
                }
                break;
              case 'lastName':
                if (nameStep === -1) {
                  nameStep = index;
                }
                break;
              default:
                inputs[component.type] = index;
                break;
            }
          }
        } else if (component.layout === 'registration-bonus-card') {
          if (marketingOfferStep === -1) {
            marketingOfferStep = index;
          }
        } else if (component.layout === 'congrats-modal') {
          if (congratsModalStep === -1) {
            congratsModalStep = index;
          }
        } else if (component.layout === 'congrats-element') {
          if (congratsElementStep === -1) {
            congratsElementStep = index;
          }
        } else if (component.layout === 'checkbox') {
          if (component.type === 'tc' && tcStep === signupStep) {
            tcStep = index;
          }
        }
        if (component.layout === 'auto-login') {
          if (autoLoginStep === -1) {
            autoLoginStep = index;
          }
        }
      });
    });

    // if last step is congrats element, final step should be the previous one, congratsElementStep is the last one
    if (congratsElementStep === signupStep) {
      signupStep--;
    }

    if (!config.useCongratsAsLastStep) {
      lastStep = signupStep;
    } else {
      lastStep = congratsElementStep;
    }

    return {
      signUp: signupStep, // last step. on next click, the signup will be triggered
      last: lastStep, // last step from the user perspective
      ocr: ocrStep,
      otp: otpStep,
      marketingOfferStep: marketingOfferStep,
      congratsModalStep: congratsModalStep,
      congratsElementStep: congratsElementStep,
      tc: tcStep,
      name: nameStep,
      inputs,
      autoLoginStep,
    };
  }, [config.steps, config.useCongratsAsLastStep, countInjectedSteps]);

  const getStepperIndex = React.useCallback(
    (currentStep: number) => {
      return config.steps[currentStep]?.stepperIndex ?? 0;
    },
    [config.steps],
  );

  const getPreviousStep = React.useCallback(
    (currentStep: number): number => {
      let found = false;
      let prevStep = currentStep - 1;
      const currentStepperIndex = config.steps[currentStep].stepperIndex;
      const prevStepperIndex = currentStepperIndex - 1;
      while (!found) {
        if (prevStep < 0) {
          prevStep = 0;
          found = true;
          break;
        }
        if (config.steps[prevStep].stepperIndex === prevStepperIndex) {
          if (config.steps[prevStep].stepperSubIndex === 0 || config.steps[prevStep].stepperSubIndex === undefined) {
            found = true;
          }
        }
        if (!found) {
          prevStep--;
        }
        if (prevStep < 0) {
          prevStep = 0;
          found = true;
        }
      }
      return prevStep;
    },
    [config.steps],
  );

  return useMemo(
    () => ({
      steps: config.steps,
      stepsMap,
      getStepperIndex,
      getPreviousStep,
      stepperContext: {
        ...config.stepperContext,
      },
      globalInputContext: config.globalInputContext,
    }),
    [config.stepperContext, config.steps, config.globalInputContext, getPreviousStep, getStepperIndex, stepsMap],
  );
}
